import React from 'react';
import type { CommentTreeOptions } from './commentTree';
import classNames from 'classnames';
import { defineStyles } from '../hooks/defineStyles';
import { useStyles } from '../hooks/useStyles';

export const HIGHLIGHT_DURATION = 3

export const CONDENSED_MARGIN_BOTTOM = 4

const styles = defineStyles("CommentFrame", (theme: ThemeType) => ({
  node: {
    border: theme.palette.border.commentBorder,
    cursor: "default",
    // Higher specificity to override child class (variant syntax)
    '&$deleted': {
      opacity: 0.6
    }
  },
  commentsNodeRoot: {
    borderRadius: theme.borderRadius.small,
  },
  child: {
    marginLeft: 8,
    marginBottom: 6,
    borderLeft: theme.palette.border.commentBorder,
    borderTop: theme.palette.border.commentBorder,
    borderBottom: theme.palette.border.commentBorder,
    borderRight: "none",
    borderRadius: "2px 0 0 2px",
  },
  new: {
    '&&': {
      borderLeft: `solid 5px ${theme.palette.secondary.light}${'8c'}`,
      '&:hover': {
        borderLeft: `solid 5px ${theme.palette.secondary.main}${'8c'}`
      },
    }
  },
  deleted: {},
  isAnswer: {
    border: theme.palette.border.answerBorder,
  },
  answerChildComment: {
    marginBottom: 8,
    border: theme.palette.border.commentBorder,
  },
  childAnswerComment: {
    borderRight: "none"
  },
  oddAnswerComment: {
    backgroundColor: theme.palette.panelBackground.default,
  },
  answerLeafComment: {
    paddingBottom: 0
  },
  isSingleLine: {
    marginBottom: 0,
    borderBottom: "none",
    borderTop: theme.palette.border.commentBorder,
    '&.comments-node-root':{
      marginBottom: CONDENSED_MARGIN_BOTTOM,
      borderBottom: theme.palette.border.commentBorder,
    }
  },
  condensed: {
    '&.comments-node-root':{
      marginBottom: CONDENSED_MARGIN_BOTTOM,
    }
  },
  shortformTop: {
    '&&': {
      marginTop: 32,
      marginBottom: 0,
    }
  },
  hoverPreview: {
    '&&': {
      marginBottom: 0
    }
  },
  moderatorHat: {
    "&.comments-node-even": {
      background: theme.palette.panelBackground.commentModeratorHat,
    },
    "&.comments-node-odd": {
      background: theme.palette.panelBackground.commentModeratorHat,
    },
  },
  '@keyframes higlight-animation': {
    from: {
      backgroundColor: theme.palette.panelBackground.commentHighlightAnimation,
      border: theme.palette.border.maxIntensity,
    },
    to: {
      backgroundColor: "none",
      border: theme.palette.border.commentBorder,
    }
  },
  highlightAnimation: {
    animation: `higlight-animation ${HIGHLIGHT_DURATION}s ease-in-out 0s;`
  },
  promoted: {
    border: `solid 1px ${theme.palette.lwTertiary.main}`,
  },
}), {
  stylePriority: -1,
});

const CommentFrame = ({
  comment,
  treeOptions,
  onClick,
  id,
  nestingLevel,
  hasChildren,
  highlighted,
  isSingleLine,
  isChild,
  isNewComment,
  isReplyToAnswer,
  hoverPreview,
  shortform,
  showPinnedOnProfile,
  children,
  className,
}: {
  comment: CommentsList,
  treeOptions: CommentTreeOptions,
  onClick?: (event: any) => void,
  id?: string,
  
  nestingLevel: number,
  hasChildren?: boolean,
  highlighted?: boolean,
  isSingleLine?: boolean,
  isChild?: boolean,
  isNewComment?: boolean,
  isReplyToAnswer?: boolean,
  hoverPreview?: boolean,
  shortform?: boolean,
  showPinnedOnProfile?: boolean,
  
  children: React.ReactNode,
  className?: string,
}) => {
  const classes = useStyles(styles);
  const { condensed, postPage, switchAlternatingHighlights } = treeOptions;
  const effectiveNestingLevel = nestingLevel + (switchAlternatingHighlights ? 1 : 0);
  
  const nodeClass = classNames(
    "comments-node",
    nestingLevelToClass(effectiveNestingLevel, classes),
    classes.node,
    className,
    comment.af && "af",
    highlighted && classes.highlightAnimation,
    isChild && classes.child,
    isNewComment && classes.new,
    comment.deleted && classes.deleted,
    comment.answer && classes.isAnswer,
    isReplyToAnswer && classes.answerChildComment,
    isChild && isReplyToAnswer && classes.childAnswerComment,
    (effectiveNestingLevel % 2 !== 0) && isReplyToAnswer && classes.oddAnswerComment,
    !hasChildren && classes.answerLeafComment,
    isSingleLine && classes.isSingleLine,
    condensed && classes.condensed,
    postPage && shortform && (effectiveNestingLevel===1) && classes.shortformTop,
    hoverPreview && classes.hoverPreview,
    comment.hideModeratorHat ? false : comment.moderatorHat && classes.moderatorHat,
    comment.promoted && classes.promoted,
  )
  
  return <div className={nodeClass} onClick={onClick} id={id}>
    {children}
  </div>
}

const nestingLevelToClass = (nestingLevel: number, classes: ClassesType<(typeof styles)["styles"]>): string => {
  return classNames(
    (nestingLevel === 1)   && classes.commentsNodeRoot,
    (nestingLevel === 1)   && "comments-node-root" ,
    (nestingLevel%2 === 0) && "comments-node-even" ,
    (nestingLevel%2 !== 0) && "comments-node-odd"  ,
    (nestingLevel > 8)  && "comments-node-its-getting-nested-here",
    (nestingLevel > 12) && "comments-node-so-take-off-all-your-margins",
    (nestingLevel > 16) && "comments-node-im-getting-so-nested",
    (nestingLevel > 20) && "comments-node-im-gonna-drop-my-margins",
    (nestingLevel > 24) && "comments-node-what-are-you-even-arguing-about",
    (nestingLevel > 28) && "comments-node-are-you-sure-this-is-a-good-idea",
    (nestingLevel > 32) && "comments-node-seriously-what-the-fuck",
    (nestingLevel > 36) && "comments-node-are-you-curi-and-lumifer-specifically",
    (nestingLevel > 40) && "comments-node-cuz-i-guess-that-makes-sense-but-like-really-tho",
  );
}

export default CommentFrame;
